<template>
    <div class="editor">
        <div class="container">
            <div class="toolbar">
                <ButtonAction v-for="(module, id) in modules"
                              :key="id"
                              :ref="'btn-' + module.title"
                              :module="module"
                              :options="mergedOptions"
                              :title="mergedOptions.locale[module.title] || module.description || ''"
                              @closeOtherDashboards="closeOtherDashboards"
                />

            </div>
        </div>
        <div ref="content"
             :contenteditable="!disabled"
             :placeholder="placeholder"
             class="content"
             tabindex="1"
        ></div>
    </div>
</template>

<script>
import { sanitizeHtml } from '@/common/comp/wysiwyg/util.js';
import { showError } from '@dsee/components/popups/toast.js';
import debounce from 'debounce';
import bus from './bus.js';
import ButtonAction from './ButtonAction.vue';
import alignCenter from './modules/alignCenter.js';

import alignLeft from './modules/alignLeft.js';
import alignRight from './modules/alignRight.js';

import bold from './modules/bold.js';
import code from './modules/code.js';


import headings from './modules/headings.vue';
import hyperlink from './modules/hyperlink.vue';

import image from './modules/image.vue';
import italic from './modules/italic.js';
import list_ordered from './modules/list_ordered.js';
import list_unordered from './modules/list_unordered.js';

import removeFormat from './modules/removeFormat.js';

import separator from './modules/separator.js';
import table from './modules/table.vue';
import underline from './modules/underline.js';


const modules = [
    [ bold, italic, underline ],
    [ alignLeft, alignCenter, alignRight ],
    [ headings, code ],
    [ hyperlink, list_ordered, list_unordered ],
    [ image, table ],
    [ removeFormat ],
];

export default {
    name: 'Wysiwyg',
    model: {
        prop: 'html',
        event: 'html',
    },
    props: {
        html: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Enter text...',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Object,
            default: () => null,
        },
    },
    components: {
        ButtonAction,
    },
    data() {
        return {
            selection: '',
        };
    },
    computed: {
        mergedOptions() {
            return Object.assign({}, bus.options, this.options);
        },
        modules() {
            const temp = [];
            for (const group of modules) {
                const list = this.filterModules(group);

                if (list.length > 0) {
                    temp.push(list);
                }
            }

            const result = [];

            for (let i = 0; i < temp.length; i++) {
                if (i > 0) {
                    result.push(separator);
                }

                for (const it of temp[i]) {
                    result.push(it);
                }
            }

            return result;
        },
        btnsWithDashboards() {
            return this.modules.filter(m => m.render);
        },
        innerHTML: {
            get() {
                return this.$refs.content.innerHTML;
            },
            set(html) {
                if (this.$refs.content.innerHTML !== html) {
                    this.$refs.content.innerHTML = html;
                }
            },
        },
    },
    methods: {
        saveSelection() {
            const selection = window.getSelection();
            if (selection && selection.getRangeAt && selection.rangeCount) {
                return selection.getRangeAt(0);
            }
            return null;
        },
        restoreSelection(range) {
            if (!range)
                return;

            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        },
        clearSelection() {
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
            }
        },
        ensureCursorInContent() {
            const selection = window.getSelection();

            if (selection && selection.rangeCount) {
                const range = selection.getRangeAt(0);
                const isCursorInContent = range.startContainer === this.$refs.content || range.endContainer === this.$refs.content;
                if (isCursorInContent)
                    return;
            }

            if (this.selection) {
                this.restoreSelection(this.selection);
                return;
            }

            const newRange = document.createRange();
            newRange.setStart(this.$refs.content, 0);
            newRange.setEnd(this.$refs.content, 0);
            selection.removeAllRanges();
            selection.addRange(newRange);
        },
        exec(command, argument, restoreSelection = true) {
            if (restoreSelection && this.selection) {
                this.restoreSelection(this.selection);
            }

            document.execCommand(command, false, argument || '');
            this.$nextTick(this.emit);
        },
        onDocumentClick(e) {
            for (let i = 0; i < this.btnsWithDashboards.length; i++) {
                const btn = this.$refs[`btn-${ this.btnsWithDashboards[i].title }`][0];
                if (btn && btn.showDashboard && !btn.$el.contains(e.target))
                    btn.closeDashboard();
            }
        },
        emit() {
            this.$emit('html', this.$refs.content.innerHTML);
            this.$emit('change', this.$refs.content.innerHTML);
        },
        onInput: debounce(function () {
            this.emit();
        }, 300),
        onFocus() {
            document.execCommand('defaultParagraphSeparator', false, this.mergedOptions.paragraphSeparator);
        },
        onContentBlur() {
            this.selection = this.saveSelection();
            this.$emit('blur', this.$refs.content);
        },
        onForcedPaste(e) {
            e.preventDefault();
            const clipboardData = e.clipboardData || window.clipboardData;
            const text = clipboardData.getData('text/plain');
            const html = sanitizeHtml(text);
            document.execCommand('insertHtml', false, html);
            this.emit();
        },
        onDefaultPaste(e) {
            e.preventDefault();

            const clipboardData = e.clipboardData || window.clipboardData;
            const files = clipboardData.files;
            if (files.length > 0) {
                this.fileAdded(files);
                return;
            }

            const source = clipboardData.getData('text/html');
            if (!source) {
                this.onForcedPaste(e);
                return;
            }

            const html = sanitizeHtml(source);
            document.execCommand('insertHTML', false, html);
            this.emit();
        },
        fileAdded(files) {
            const file = files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                const img = document.createElement('img');
                img.src = reader.result;
                img.onload = () => {
                    document.execCommand('insertImage', false, img.src);
                };
                img.onerror = () => {
                    showError('Не удалось вставить изображение');
                };
            }, false);
            reader.readAsDataURL(file);
        },
        syncHTML() {
            if (this.$refs.content.innerHTML.trim() === '<br>') {
                this.innerHTML = this.html.replace('<br>', '');
                this.emit();
            } else if (this.html !== this.$refs.content.innerHTML) {
                this.innerHTML = this.html;
            }
        },
        filterModules(modules) {
            const hidden = this.mergedOptions.hideModules ?? {};
            return modules.filter(it => !hidden[it.title]);
        },
        onKeyEvent(e) {
            if (e.code === 'Tab') {
                if (bus.utils.isNodeInList()) {
                    e.preventDefault();
                    this.exec('indent', null, false);
                }
            } else if (e.code === 'Backspace') {
                if (bus.utils.isNodeStartOfList()) {
                    e.preventDefault();
                    this.exec('outdent', null, false);
                }
            }
        },
        closeOtherDashboards(currentDashboard) {
            for (let i = 0; i < this.btnsWithDashboards.length; i++) {
                const btn = this.$refs[`btn-${ this.btnsWithDashboards[i].title }`];
                if (btn && this.btnsWithDashboards[i].title !== currentDashboard) {
                    btn[0].opened = false;
                }
            }
        },
    },
    mounted() {
        this.$options.unwatch = this.$watch('html', this.syncHTML, { immediate: true });

        document.addEventListener('click', this.onDocumentClick);

        this.$refs.content.addEventListener('focus', this.onFocus);
        this.$refs.content.addEventListener('input', this.onInput);
        this.$refs.content.addEventListener('blur', this.onContentBlur, { capture: true });
        this.$refs.content.addEventListener('keydown', this.onKeyEvent, true);

        if (this.mergedOptions.forcePlainTextOnPaste === true) {
            this.$refs.content.addEventListener('paste', this.onForcedPaste);
        } else {
            this.$refs.content.addEventListener('paste', this.onDefaultPaste);
        }

        this.$refs.content.style.maxHeight = this.mergedOptions.maxHeight;
    },
    beforeDestroy() {
        this.$options.unwatch();
        document.removeEventListener('click', this.onDocumentClick);

        this.$refs.content.removeEventListener('blur', this.onContentBlur);
        this.$refs.content.removeEventListener('input', this.onInput);
        this.$refs.content.removeEventListener('focus', this.onFocus);
        this.$refs.content.removeEventListener('keydown', this.onKeyEvent, true);

        if (this.mergedOptions.forcePlainTextOnPaste === true) {
            this.$refs.content.removeEventListener('paste', this.onForcedPaste);
        } else {
            this.$refs.content.removeEventListener('paste', this.onDefaultPaste);
        }
    },
};
</script>
<style lang="scss">
@import "@dsee/mixins.scss";

.editor {
    --border-radius: 16px;
    --border-color: rgba(16, 24, 40, 0.25);

    width: 100%;
    transition: background-color var(--transition-function);
    position: relative;

    .container {
        background: #FFFFFF;
        position: sticky;
        top: 0;
    }

    .toolbar {
        background: #FFFFFF;
        border: 1px solid var(--border-color);
        border-bottom: 0;
        border-top-left-radius: var(--border-radius);
        border-top-right-radius: var(--border-radius);
        box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
        display: flex;
        height: 32px;
        padding: 0 4px;

        .dashboard {
            width: 100%;
            position: absolute;
            left: 0;
            text-align: left;
            padding: 8px 16px;
            background: rgba(255, 255, 255, 1);
            border: 1px solid var(--border-color);
            border-top: none;
            border-bottom-left-radius: var(--border-radius);
            border-bottom-right-radius: var(--border-radius);
            box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
            z-index: 1;

        }
    }

    .content {
        border: 1px solid var(--border-color);
        border-bottom-left-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
        min-height: 150px;
        font-family: inherit;
        color: inherit;
        overflow-y: auto;
        padding: 16px;
        overflow-wrap: anywhere;
        resize: vertical;
        transition: var(--transition-function);
        transition-property: border-color, background-color;
        @include scrollbar;

        &:focus {
            outline: 0;
            border: var(--input-focus-border);
        }

        @import "wysiwyg.styles";

        ol,
        ul {
            padding-left: 0;
        }
    }
    .content[contenteditable=true]:empty {
        &:before {
            content: attr(placeholder);
            color: var(--input-placeholder-color);
            display: block;
        }
    }

    .content table th,
    .content table td {
        border: 1px solid #ddd;
        padding: 2px;
    }

    .content ul li,
    .content ol li {
        list-style-position: inside;
    }

    &.error {
        border-radius: var(--border-radius);
        background: var(--input-error-background);

        .toolbar {
            background: var(--input-error-background);
            border: var(--input-error-border);
        }

        .content {
            border: var(--input-error-border);
            border-top: 0;
        }
    }
}

@media screen and (max-width: 320px) {
    .toolbar a {
        margin: 0 2px;
    }

    .toolbar a.vw-btn-separator {
        display: none;
    }
}
</style>
